// Handwritten note: paid note added to the order from the cart drawer / cart page.
// Spec: docs/superpowers/specs/2026-10-07-handwritten-note-design.md
// Everything lives inside the guard: on /cart this file is included twice (drawer and page).
if (!customElements.get('handwritten-note')) {
  customElements.define(
    'handwritten-note',
    class HandwrittenNote extends HTMLElement {
      static DRAFT_KEY = 'handwritten-note-draft';
      static orphanRemoval = null;

      connectedCallback() {
        this.checkbox = this.querySelector('[data-hn-toggle]');
        this.panel = this.querySelector('[data-hn-form]');
        this.textarea = this.querySelector('[data-hn-message]');
        this.counter = this.querySelector('[data-hn-count]');
        this.submitButton = this.querySelector('[data-hn-submit]');
        this.errorEl = this.querySelector('[data-hn-error]');
        this.editButton = this.querySelector('[data-hn-edit]');
        this.removeButton = this.querySelector('[data-hn-remove]');
        this.savedText = this.querySelector('[data-hn-text]');
        this.busy = false;

        this.checkbox?.addEventListener('change', () => this.setOpen(this.checkbox.checked, true));
        this.editButton?.addEventListener('click', () => this.setOpen(this.panel.hidden, true));
        this.removeButton?.addEventListener('click', () => this.removeNote(true));
        this.textarea.addEventListener('input', () => {
          this.updateCount();
          this.saveDraft();
        });
        this.submitButton.addEventListener('click', () => this.submit());

        // The note is the only line left: take it out. Once, even if the drawer and the page both render it.
        // Listeners are bound first so Edit and Remove still work if this removal fails.
        if (this.hasAttribute('data-orphan')) {
          // On /cart both boxes can be orphaned. The page box removes it, so the cart page (not the hidden drawer) re-renders.
          const pageOwns =
            this.dataset.context === 'drawer' && document.querySelector('#main-cart-footer handwritten-note[data-orphan]');
          if (!pageOwns && !HandwrittenNote.orphanRemoval) {
            HandwrittenNote.orphanRemoval = this.removeNote(false).finally(() => {
              HandwrittenNote.orphanRemoval = null;
            });
          }
          return;
        }

        this.restoreDraft();
        this.updateCount();
      }

      setOpen(open, moveFocus) {
        this.panel.hidden = !open;
        if (this.checkbox) this.checkbox.checked = open;
        this.editButton?.setAttribute('aria-expanded', String(open));
        if (open && this.editButton && !this.textarea.value) {
          this.textarea.value = this.savedText ? this.savedText.textContent.trim() : '';
        }
        this.updateCount();
        this.saveDraft();
        if (open && moveFocus) this.textarea.focus();
      }

      updateCount() {
        this.counter.textContent = `${this.textarea.value.length} / ${this.textarea.maxLength}`;
      }

      saveDraft() {
        try {
          sessionStorage.setItem(
            HandwrittenNote.DRAFT_KEY,
            JSON.stringify({ open: !this.panel.hidden, text: this.textarea.value })
          );
        } catch (e) {}
      }

      restoreDraft() {
        let draft = null;
        try {
          draft = JSON.parse(sessionStorage.getItem(HandwrittenNote.DRAFT_KEY));
        } catch (e) {}
        if (!draft) return;
        this.textarea.value = draft.text || '';
        if (draft.open) this.setOpen(true, false);
      }

      clearDraft() {
        try {
          sessionStorage.removeItem(HandwrittenNote.DRAFT_KEY);
        } catch (e) {}
      }

      submit() {
        const text = this.textarea.value.trim();
        if (!text) {
          this.showError(this.dataset.errorEmpty);
          this.textarea.focus();
          return;
        }
        const properties = { Message: text };
        if (this.dataset.lineKey) {
          this.request(routes.cart_change_url, { id: this.dataset.variantId, quantity: 1, properties }, this.focusId('edit'));
        } else {
          const items = [{ id: Number(this.dataset.variantId), quantity: 1, properties }];
          this.request(`${routes.cart_add_url}.js`, { items }, this.focusId('edit'));
        }
      }

      // Edit and remove name the note by its variant id, sent as a string: on the live store a numeric
      // id was rejected (400). The line key changes every time the message changes, so a key held by a
      // box that was not redrawn goes stale; the variant id never does. Tested live (2026-10-08): with
      // two note lines, an edit leaves one line with the new message and a remove clears both.
      removeNote(moveFocus) {
        return this.request(
          routes.cart_change_url,
          { id: this.dataset.variantId, quantity: 0 },
          moveFocus ? this.focusId('toggle') : null
        );
      }

      focusId(part) {
        return `HandwrittenNote-${this.dataset.context}-${part}`;
      }

      // On page load this script can run before cart-drawer.js has defined <cart-drawer-items>,
      // so wait for the host to be upgraded before calling its methods.
      async cartHost() {
        const tag = this.closest('cart-drawer') ? 'cart-drawer-items' : 'cart-items';
        const host = document.querySelector(tag);
        if (!host) return null;
        await customElements.whenDefined(tag);
        return host;
      }

      async request(url, payload, focusId) {
        if (this.busy) return;
        this.busy = true;
        this.setLoading(true);
        this.showError('');

        let host;
        let targets;
        let state;
        try {
          host = await this.cartHost();
          targets = host ? host.getSectionsToRender() : [];
          const body = JSON.stringify({
            ...payload,
            sections: targets.map((target) => target.section),
            sections_url: window.location.pathname,
          });
          const response = await fetch(url, { ...fetchConfig(), body });
          state = await response.json();
          if (!response.ok || state.status || state.errors) {
            throw new Error(state.description || state.errors || state.message || `Cart request failed (${response.status})`);
          }
        } catch (error) {
          console.error(error);
          this.showError(this.dataset.errorGeneric);
          this.busy = false;
          this.setLoading(false);
          return;
        }

        // Shopify has saved the change, so nothing below may report a failure.
        this.clearDraft();
        this.busy = false;
        this.setLoading(false);
        try {
          this.render(host, targets, state);
          this.restoreFocus(focusId);
        } catch (error) {
          // The cart on screen would be out of date: reload it rather than show a false error.
          console.error(error);
          this.reloadPage();
          return;
        }
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        } catch (error) {
          // Another script's cartUpdate listener failed. The note is saved and already shown.
          console.error(error);
        }
      }

      reloadPage() {
        window.location.reload();
      }

      // Same swap as CartItems.updateQuantity (assets/cart.js).
      render(host, targets, state) {
        if ('item_count' in state) {
          const empty = state.item_count === 0;
          document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
          document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
          host?.classList.toggle('is-empty', empty);
        }
        targets.forEach((target) => {
          const html = state.sections && state.sections[target.section];
          const container = document.getElementById(target.id);
          if (!html || !container) return;
          const element = container.querySelector(target.selector) || container;
          element.innerHTML = host.getSectionInnerHTML(html, target.selector);
        });
      }

      // After a swap, put focus back. In an open drawer, re-apply its focus trap the way
      // CartItems.updateQuantity does (assets/cart.js:202-211); the old trap points at replaced nodes.
      restoreFocus(focusId) {
        const target = focusId ? document.getElementById(focusId) : null;
        const drawer = this.dataset.context === 'drawer' ? document.querySelector('cart-drawer') : null;
        if (!drawer || !drawer.classList.contains('active')) {
          target?.focus();
          return;
        }
        if (drawer.classList.contains('is-empty')) {
          trapFocus(drawer.querySelector('.drawer__inner-empty'), drawer.querySelector('a'));
        } else if (target) {
          trapFocus(drawer, target);
        }
      }

      setLoading(on) {
        this.submitButton.classList.toggle('loading', on);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
        [this.submitButton, this.removeButton].forEach((button) => {
          if (!button) return;
          if (on) button.setAttribute('aria-disabled', 'true');
          else button.removeAttribute('aria-disabled');
        });
      }

      showError(message) {
        // A single space keeps the alert region in the layout (Refresh hides p:empty).
        this.errorEl.textContent = message || ' ';
      }
    }
  );
}